// typing-indicator.tsx — rotating-status spec (2026-08-22). The one "still working" visual
// shared by the dashboard's in-app playground and the public playground (apps/web) — same
// component, so the two surfaces can never independently drift on what "thinking" vs
// "searching" looks like. This is the React-side twin of `apps/widget/src/ui/chat.ts`'s
// `renderTyping`: same two states, same rule ("no dots after the text" — the sentence already
// ends with "…"), just built from DOM nodes there and JSX here.
//
// Reuses `st-bounce` (globals.css) rather than defining a new keyframe — that's the same
// vertical-bounce-with-opacity animation `StatusChip`'s working-state dot already uses, so
// "an ambient dot is animating" reads consistently everywhere in the dashboard, and it already
// carries its own `prefers-reduced-motion` kill switch.
import { cn } from '../lib/utils.ts';

export function TypingIndicator({
  /** The sentence to show next to a single pulsing accent dot (rotating-status spec §A state
   * 2, "searching"). Omit entirely for state 1 ("thinking, no tool yet"), which renders the
   * bare three-dot bounce with no text — the two states are mutually exclusive, never both. */
  sentence,
  className,
}: {
  sentence?: string;
  className?: string;
}) {
  if (sentence) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-muted-foreground', className)}>
        <span aria-hidden="true" className="st-dot-active size-1.5 shrink-0 rounded-full bg-primary" />
        {sentence}
      </span>
    );
  }
  return (
    <span aria-hidden="true" className={cn('inline-flex items-center gap-1', className)}>
      <span className="st-dot-active size-1.5 rounded-full bg-muted-foreground" />
      <span className="st-dot-active size-1.5 rounded-full bg-muted-foreground [animation-delay:0.15s]" />
      <span className="st-dot-active size-1.5 rounded-full bg-muted-foreground [animation-delay:0.3s]" />
    </span>
  );
}
